import { ANNOTATION_TOOLS } from "./constants";
import { ChatBubbleOutlineRounded as CommentIcon } from "@mui/icons-material";
import { Box, Paper, Stack, Tooltip, Typography } from "@mui/material";
import { useState, useEffect } from "react";

const ResizeHandle = ({ position, onResizeStart, color, shape = "box" }) => {
  // For circles, only show corner handles
  const circleHandles = ["nw", "ne", "sw", "se"];
  // For lines/arrows, only show left and right
  const lineHandles = ["w", "e"];

  if (shape === "circle" && !circleHandles.includes(position)) return null;
  if (
    (shape === "line" || shape === "arrow") &&
    !lineHandles.includes(position)
  )
    return null;

  const positionStyles = {
    nw: { top: -6, left: -6, cursor: "nw-resize" },
    ne: { top: -6, right: -6, cursor: "ne-resize" },
    sw: { bottom: -6, left: -6, cursor: "sw-resize" },
    se: { bottom: -6, right: -6, cursor: "se-resize" },
    n: {
      top: -6,
      left: "50%",
      transform: "translateX(-50%)",
      cursor: "n-resize",
    },
    s: {
      bottom: -6,
      left: "50%",
      transform: "translateX(-50%)",
      cursor: "s-resize",
    },
    e: {
      right: -6,
      top: "50%",
      transform: "translateY(-50%)",
      cursor: "e-resize",
    },
    w: {
      left: -6,
      top: "50%",
      transform: "translateY(-50%)",
      cursor: "w-resize",
    },
  };

  return (
    <Box
      onMouseDown={(e) => {
        e.stopPropagation();
        e.preventDefault();
        onResizeStart(e, position);
      }}
      sx={{
        position: "absolute",
        width: 12,
        height: 12,
        backgroundColor: "white",
        border: `2px solid ${color}`,
        borderRadius: shape === "circle" ? "50%" : "2px",
        zIndex: 100,
        boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
        ...positionStyles[position],
        "&:hover": {
          backgroundColor: color,
          transform: positionStyles[position].transform
            ? `${positionStyles[position].transform} scale(1.2)`
            : "scale(1.2)",
        },
      }}
    />
  );
};

const AnnotationOverlay = ({
  annotation,
  onRemove,
  onMove,
  onResize,
  zoom,
  containerRef,
}) => {
  const tool = ANNOTATION_TOOLS.find((t) => t.id === annotation.type);
  const color = tool?.color || "#666";
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isSelected, setIsSelected] = useState(false);

  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    setIsSelected(true);

    const handleMouseMove = (moveEvent) => {
      if (!containerRef?.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = ((moveEvent.clientX - rect.left) / rect.width) * 100;
      const y = ((moveEvent.clientY - rect.top) / rect.height) * 100;
      onMove?.(annotation.id, {
        x: Math.max(2, Math.min(98, x)),
        y: Math.max(1, Math.min(99, y)),
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleResizeStart = (e, handle) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setIsSelected(true);

    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = annotation.width || getDefaultWidth(annotation.type);
    const startHeight = annotation.height || getDefaultHeight(annotation.type);
    const startPosX = annotation.x;
    const startPosY = annotation.y;

    const handleMouseMove = (moveEvent) => {
      if (!containerRef?.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      // Raw pixel deltas from mouse movement, adjusted for zoom
      const deltaX = (moveEvent.clientX - startX) / (zoom || 1);
      const deltaY = (moveEvent.clientY - startY) / (zoom || 1);

      let newWidth = startWidth;
      let newHeight = startHeight;
      // Position offsets in pixels (to keep the opposite edge anchored)
      let offsetX = 0;
      let offsetY = 0;

      if (annotation.type === "circle") {
        // Circles: uniform resize, anchor opposite corner
        const absDelta = Math.max(Math.abs(deltaX), Math.abs(deltaY));
        const growSign = handle.includes("e") || handle.includes("s") ? 1 : -1;
        newWidth = Math.max(30, startWidth + absDelta * growSign);
        newHeight = newWidth;
        const dW = newWidth - startWidth;
        // Shift center so opposite corner stays fixed
        offsetX = handle.includes("e")
          ? dW / 2
          : handle.includes("w")
          ? -dW / 2
          : 0;
        offsetY = handle.includes("s")
          ? dW / 2
          : handle.includes("n")
          ? -dW / 2
          : 0;
      } else if (annotation.type === "line" || annotation.type === "arrow") {
        if (handle.includes("e")) {
          newWidth = Math.max(30, startWidth + deltaX);
          offsetX = (newWidth - startWidth) / 2;
        } else if (handle.includes("w")) {
          newWidth = Math.max(30, startWidth - deltaX);
          offsetX = -(newWidth - startWidth) / 2;
        }
      } else {
        // Box and highlight: directional resize
        if (handle.includes("e")) {
          newWidth = Math.max(30, startWidth + deltaX);
          offsetX = (newWidth - startWidth) / 2;
        } else if (handle.includes("w")) {
          newWidth = Math.max(30, startWidth - deltaX);
          offsetX = -(newWidth - startWidth) / 2;
        }
        if (handle.includes("s")) {
          newHeight = Math.max(20, startHeight + deltaY);
          offsetY = (newHeight - startHeight) / 2;
        } else if (handle.includes("n")) {
          newHeight = Math.max(20, startHeight - deltaY);
          offsetY = -(newHeight - startHeight) / 2;
        }
      }

      // Convert pixel offsets to percentage position shifts
      const newPosX = startPosX + (offsetX / rect.width) * 100;
      const newPosY = startPosY + (offsetY / rect.height) * 100;

      onResize?.(annotation.id, { width: newWidth, height: newHeight });
      onMove?.(annotation.id, {
        x: Math.max(1, Math.min(99, newPosX)),
        y: Math.max(1, Math.min(99, newPosY)),
      });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleClick = (e) => {
    e.stopPropagation();
    setIsSelected(true);
  };

  const handleDoubleClick = (e) => {
    e.stopPropagation();
    onRemove(annotation.id);
  };

  useEffect(() => {
    const handleDocumentClick = () => setIsSelected(false);
    document.addEventListener("click", handleDocumentClick);
    return () => document.removeEventListener("click", handleDocumentClick);
  }, []);

  const getDefaultWidth = (type) => {
    switch (type) {
      case "circle":
        return 60;
      case "box":
        return 80;
      case "line":
        return 80;
      case "arrow":
        return 72;
      case "highlight":
        return 100;
      default:
        return 60;
    }
  };

  const getDefaultHeight = (type) => {
    switch (type) {
      case "circle":
        return 60;
      case "box":
        return 50;
      case "line":
        return 3;
      case "arrow":
        return 3;
      case "highlight":
        return 24;
      default:
        return 50;
    }
  };

  const baseSx = {
    position: "absolute",
    left: `${annotation.x}%`,
    top: `${annotation.y}%`,
    cursor: isDragging ? "grabbing" : "grab",
    userSelect: "none",
    transition: isDragging || isResizing ? "none" : "transform 0.1s ease",
    zIndex: isSelected || isResizing ? 50 : 1,
  };

  const renderResizeHandles = (shape) => {
    if (!isSelected && !isResizing) return null;

    const handles = ["nw", "ne", "sw", "se", "n", "s", "e", "w"];
    return handles.map((pos) => (
      <ResizeHandle
        key={pos}
        position={pos}
        onResizeStart={handleResizeStart}
        color={color}
        shape={shape}
      />
    ));
  };

  const renderAnnotation = () => {
    const width = annotation.width || getDefaultWidth(annotation.type);
    const height = annotation.height || getDefaultHeight(annotation.type);

    switch (annotation.type) {
      case "circle":
        return (
          <Box
            onClick={handleClick}
            onMouseDown={handleMouseDown}
            onDoubleClick={handleDoubleClick}
            sx={{
              ...baseSx,
              transform: "translate(-50%, -50%)",
              width,
              height,
              border: `3px solid ${color}`,
              borderRadius: "50%",
              backgroundColor: isDragging
                ? `${color}30`
                : isSelected
                ? `${color}20`
                : `${color}10`,
              cursor: isDragging ? "grabbing" : "grab",
              "&:hover": { backgroundColor: `${color}25` },
            }}
          >
            {renderResizeHandles("circle")}
          </Box>
        );

      case "box":
        return (
          <Box
            onClick={handleClick}
            onMouseDown={handleMouseDown}
            onDoubleClick={handleDoubleClick}
            sx={{
              ...baseSx,
              transform: "translate(-50%, -50%)",
              width,
              height,
              border: `3px solid ${color}`,
              backgroundColor: isDragging
                ? `${color}30`
                : isSelected
                ? `${color}20`
                : `${color}10`,
              cursor: isDragging ? "grabbing" : "grab",
              "&:hover": { backgroundColor: `${color}25` },
            }}
          >
            {renderResizeHandles("box")}
          </Box>
        );

      case "line":
        return (
          <Box
            onClick={handleClick}
            sx={{
              ...baseSx,
              width,
              height: 12,
              cursor: "default",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Box
              onMouseDown={handleMouseDown}
              onDoubleClick={handleDoubleClick}
              sx={{
                width: "100%",
                height: isDragging || isSelected ? 6 : 3,
                backgroundColor: color,
                cursor: isDragging ? "grabbing" : "grab",
                transition: "height 0.1s ease",
                "&:hover": { height: 5 },
              }}
            />
            {renderResizeHandles("line")}
          </Box>
        );

      case "arrow":
        return (
          <Box
            onClick={handleClick}
            sx={{
              ...baseSx,
              width,
              height: 20,
              cursor: "default",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Box
              onMouseDown={handleMouseDown}
              onDoubleClick={handleDoubleClick}
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: isDragging ? "grabbing" : "grab",
                transform: isDragging ? "scale(1.1)" : "none",
              }}
            >
              <Box
                sx={{ width: width - 12, height: 3, backgroundColor: color }}
              />
              <Box
                sx={{
                  width: 0,
                  height: 0,
                  borderTop: "8px solid transparent",
                  borderBottom: "8px solid transparent",
                  borderLeft: `12px solid ${color}`,
                  marginLeft: "-2px",
                }}
              />
            </Box>
            {renderResizeHandles("arrow")}
          </Box>
        );

      case "highlight":
        return (
          <Box
            onClick={handleClick}
            onMouseDown={handleMouseDown}
            onDoubleClick={handleDoubleClick}
            sx={{
              ...baseSx,
              transform: "translate(-50%, -50%)",
              width,
              height,
              backgroundColor: isDragging
                ? `${color}80`
                : isSelected
                ? `${color}70`
                : `${color}60`,
              cursor: isDragging ? "grabbing" : "grab",
              "&:hover": { backgroundColor: `${color}80` },
            }}
          >
            {renderResizeHandles("box")}
          </Box>
        );

      case "comment":
        return (
          <Tooltip
            title={`${annotation.text || "Comment"} (double-click to delete)`}
            arrow
            placement={"top"}
          >
            <Paper
              elevation={isDragging ? 6 : 3}
              onMouseDown={handleMouseDown}
              onDoubleClick={handleDoubleClick}
              sx={{
                ...baseSx,
                transform: `translate(-50%, -50%)${
                  isDragging ? " scale(1.1)" : ""
                }`,
                p: 1,
                backgroundColor: color,
                color: "white",
                borderRadius: 2,
                maxWidth: 200,
                "&:hover": { transform: "translate(-50%, -50%) scale(1.05)" },
              }}
            >
              <Stack direction={"row"} alignItems={"flex-start"} gap={0.5}>
                <CommentIcon sx={{ fontSize: 16 }} />
                <Typography
                  variant={"caption"}
                  sx={{
                    fontSize: `${0.7 * (1 / zoom)}rem`,
                    lineHeight: 1.3,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {annotation.text || "Comment"}
                </Typography>
              </Stack>
            </Paper>
          </Tooltip>
        );

      default:
        return null;
    }
  };

  return renderAnnotation();
};

export default AnnotationOverlay;
